import type { MeResponse, StatusResponse } from '@xchange/shared';
import type { ReactNode } from 'react';
import { Logo } from './Logo.js';

/**
 * The site around the app in a browser: a top bar with the mark and the
 * the account, the content, and a footer with the links. Nothing in the top
 * bar leads away from trading; the site's pages are in the footer. Trading takes the
 * whole width; the moments that are the same everywhere (signing in, a new
 * wallet's words) sit in one calm column. Anything that is not trading opens
 * in a new tab; this screen stays on the trade.
 */
export function WebShell({
  me,
  status: _status,
  wide = false,
  onHome,
  onSignOut,
  children,
}: {
  me: MeResponse | null;
  status: StatusResponse | null;
  wide?: boolean;
  onHome: () => void;
  onSignOut: () => void;
  children: ReactNode;
}) {
  return (
    <div className="site">
      <header className="site-top" data-testid="site-top">
        <button
          className="site-brand"
          onClick={onHome}
          aria-label="xchange home"
          data-testid="site-home"
        >
          <Logo height={26} />
          <span>xchange</span>
        </button>
        <div className="site-account">
          {me !== null && (
            <>
              <span className="site-name" title={me.telegramId} data-testid="site-name">
                {me.displayName}
              </span>
              <button
                className="site-signout"
                onClick={onSignOut}
                title="Lock: your PIN opens it again"
                data-testid="site-lock"
              >
                Lock
              </button>
            </>
          )}
        </div>
      </header>
      <main className={`site-main${wide ? ' wide' : ''}`}>{children}</main>
      <footer className="site-foot">
        <span className="site-tag">Where communities meet liquidity</span>
        <nav aria-label="Links">
          <a href="https://xchangehq.com" target="_blank" rel="noreferrer">
            xchangehq.com
          </a>
          <a href="https://x.com/xchange_hq" target="_blank" rel="noreferrer">
            X
          </a>
          <a href="https://www.tiktok.com/@xchange_hq" target="_blank" rel="noreferrer">
            TikTok
          </a>
          <a href="https://xchangehq.com/xge" target="_blank" rel="noreferrer">
            XGE
          </a>
          <a href="https://xchangehq.com/faq" target="_blank" rel="noreferrer">
            FAQ
          </a>
          <a href="https://xchangehq.com/security" target="_blank" rel="noreferrer">
            Security
          </a>
          <a href="https://xchangehq.com/terms" target="_blank" rel="noreferrer">
            Terms
          </a>
          <a href="https://xchangehq.com/privacy" target="_blank" rel="noreferrer">
            Privacy
          </a>
        </nav>
      </footer>
    </div>
  );
}
